/**
 * Candidate scoring used to pick the strongest preprocessing/segmentation pass.
 * Scoring is purely statistical — text is never rewritten or invented.
 */

const DEVANAGARI = /[\u0900-\u097F]/u;
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;

export function usefulCharacterCount(text: string): number {
  return (text.match(/[\p{L}\p{N}]/gu) || []).length;
}

/** Share of letters/digits that belong to the Devanagari block. */
export function devanagariRatio(text: string): number {
  const letters = text.match(/[\p{L}\p{N}]/gu) || [];
  if (!letters.length) return 0;
  const deva = letters.filter((c) => DEVANAGARI.test(c)).length;
  return deva / letters.length;
}

/** Counts standalone junk fragments such as lone Latin letters or symbols. */
export function isolatedFragmentCount(text: string, expectDevanagari: boolean): number {
  const words = text.split(/\s+/).filter(Boolean);
  let junk = 0;
  words.forEach((word) => {
    const stripped = word.replace(/[^\p{L}\p{N}]/gu, '');
    if (!stripped) {
      junk += 1;
      return;
    }
    if (stripped.length === 1 && !DEVANAGARI.test(stripped)) {
      junk += 1;
      return;
    }
    if (expectDevanagari && stripped.length <= 2 && /^[A-Za-z]+$/.test(stripped)) junk += 1;
  });
  return junk;
}

export function fragmentedLineCount(text: string): number {
  return text
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .filter((line) => (line.match(LETTER_OR_DIGIT) || []).length <= 1).length;
}

export interface Candidate {
  text: string;
  averageConfidence: number | null;
  lineCount: number;
}

export interface ScoreOptions {
  /** True when the selected language configuration is Devanagari-only. */
  expectDevanagari: boolean;
}

export interface ScoreBreakdown {
  score: number;
  confidence: number;
  coverage: number;
  devanagariRatio: number;
  junkPenalty: number;
  fragmentPenalty: number;
}

/**
 * Higher is better. Combines engine confidence, useful text coverage, script
 * validity and penalties for junk fragments.
 */
export function scoreCandidate(candidate: Candidate, options: ScoreOptions): ScoreBreakdown {
  const { text } = candidate;
  const coverage = usefulCharacterCount(text);
  const confidence = candidate.averageConfidence ?? 50;
  const ratio = devanagariRatio(text);

  let score = confidence * Math.log10(10 + coverage);

  if (options.expectDevanagari) {
    // Reward passes that actually produced Devanagari rather than Latin noise.
    score *= 0.35 + 0.65 * ratio;
  }

  const junk = isolatedFragmentCount(text, options.expectDevanagari);
  const junkPenalty = Math.min(score * 0.4, junk * (options.expectDevanagari ? 6 : 3));
  const fragments = fragmentedLineCount(text);
  const fragmentPenalty = Math.min(score * 0.3, fragments * 4);

  score = Math.max(0, score - junkPenalty - fragmentPenalty);
  if (!coverage) score = 0;

  return {
    score,
    confidence,
    coverage,
    devanagariRatio: ratio,
    junkPenalty,
    fragmentPenalty,
  };
}

export function pickStrongest<T extends { candidate: Candidate }>(
  entries: T[],
  options: ScoreOptions,
): { entry: T; breakdown: ScoreBreakdown } | null {
  let best: { entry: T; breakdown: ScoreBreakdown } | null = null;
  entries.forEach((entry) => {
    const breakdown = scoreCandidate(entry.candidate, options);
    if (!best || breakdown.score > best.breakdown.score) best = { entry, breakdown };
  });
  return best;
}
